import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Star, CheckCircle2, Briefcase, Target, Cpu, Calendar, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { companyInfo, whyChooseUs, processSteps, testimonials } from "../data";
import useMetadata from "../hooks/useMetadata";

// Map icon names to Lucide components
const iconMap = {
  Briefcase: Briefcase,
  Target: Target,
  Cpu: Cpu,
  Calendar: Calendar,
  ShieldCheck: ShieldCheck,
};

export default function Home() {
  useMetadata(
    "Professionelt jord-, grave- og anlægsarbejde",
    "Godske Byg & Anlæg udfører professionelt jord-, grave- og anlægsarbejde med fokus på kvalitet, holdbarhed og præcision. Kontakt os for et uforpligtende tilbud."
  );

  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getVisibleCount = () => {
    if (windowWidth >= 1024) return 3; // lg
    if (windowWidth >= 640) return 2;  // sm/md
    return 1;                          // mobile
  };

  const visibleCount = getVisibleCount();
  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  useEffect(() => {
    if (testimonialIndex > maxIndex) {
      setTestimonialIndex(maxIndex);
    }
  }, [visibleCount, maxIndex, testimonialIndex]);

  useEffect(() => {
    async function fetchData() {
      try {
        const [servicesRes, projectsRes] = await Promise.all([
          fetch("https://api.dev.auxo.dk/public/content/ydelser"),
          fetch("https://api.dev.auxo.dk/public/v2/content/projekter"),
        ]);

        const servicesData = await servicesRes.json();
        const projectsData = await projectsRes.json();

        setServices(servicesData || []);
        setProjects(projectsData.items || []);
      } catch (error) {
        console.error("Fejl ved hentning af CMS-data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative flex min-h-[55vh] items-center justify-center overflow-hidden bg-brand-dark py-16">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 bg-black">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/b6ab12dc-9087-41bb-8b7f-45b54510818a/gravemaskine-arbejde.jpg"
            alt="Godske Byg & Anlæg - Gravemaskine i arbejde"
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/50 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl lg:text-5xl max-w-4xl mx-auto leading-tight uppercase">
            Specialister i udgravning, fundamenter og jordarbejde
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-white leading-relaxed">
            Godske Byg & Anlæg leverer millimeterpræcist arbejde til private og erhverv. Vi danner det solide grundlag for dit næste byggeprojekt.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded bg-brand-accent px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-accent-hover hover:scale-[1.02] active:scale-[0.98]"
            >
              Få et tilbud
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/projekter"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded bg-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-brand-charcoal transition-all hover:bg-gray-100 hover:scale-[1.02] active:scale-[0.98] shadow-sm"
            >
              Se projekter
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative bg-gray-50 pt-12 pb-20 sm:pt-16 sm:pb-28">
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-64 rounded-none bg-white shadow-[0_0_25px_rgba(0,0,0,0.12)] flex flex-col md:flex-row overflow-hidden">
                    <div className="w-full md:w-1/2 bg-gray-200 h-48 md:h-auto" />
                    <div className="w-full md:w-1/2 p-8 flex flex-col justify-between">
                      <div className="space-y-3">
                        <div className="h-6 w-40 bg-gray-200 rounded-none" />
                      </div>
                      <div className="h-10 w-10 bg-gray-200 rounded-none self-end" />
                    </div>
                  </div>
                ))
              : services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/ydelser/${service.url}`}
                    className="group relative flex flex-col md:flex-row overflow-hidden rounded-none bg-white shadow-[0_0_25px_rgba(0,0,0,0.12)] hover:shadow-[0_0_35px_rgba(0,0,0,0.18)] transition-all duration-300 min-h-[280px]"
                  >
                    {/* Image Column */}
                    <div className="relative w-full md:w-1/2 h-56 md:h-auto overflow-hidden">
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,height=600,fit=cover,quality=80,format=auto/${service.hero_billede[0].replace(
                          "https://assets.auxodev.dk/",
                          ""
                        )}`}
                        alt={service.titel.da}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>

                    {/* Content Column */}
                    <div className="relative w-full md:w-1/2 p-6 sm:p-8 flex flex-col justify-between">
                      <div className="flex-1 flex flex-col justify-center">
                        <h3 className="relative z-10 text-xl sm:text-2xl font-bold text-brand-charcoal uppercase leading-snug group-hover:text-brand-accent transition-colors">
                          {service.titel.da}
                        </h3>
                      </div>

                      <div className="mt-6 flex items-center">
                        <span className="text-xs font-bold uppercase tracking-wider text-brand-charcoal/70 group-hover:text-brand-accent transition-colors">
                          Læs mere
                        </span>
                        {/* Floating circular button in the bottom right corner */}
                        <div className="absolute bottom-6 right-6 bg-brand-accent text-white p-3 rounded-none shadow-md group-hover:bg-brand-charcoal group-hover:scale-110 transition-all duration-300">
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
          </div>
      </div>

      {/* Asymmetric excavated ground line bottom edge - taller, slightly jagged, positioned behind content */}
      <div className="absolute bottom-0 left-0 right-0 h-44 sm:h-60 lg:h-72 z-0 pointer-events-none overflow-hidden">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="absolute bottom-0 w-full h-full text-[#4c2223] fill-current"
        >
          <path d="M 0 120 L 1200 120 L 1200 15 L 850 70 L 500 5 L 0 50 Z" />
        </svg>
      </div>
    </section>

      {/* About Section on Homepage */}
      <section className="bg-white pt-8 sm:pt-12 pb-8">
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          {/* Rounded Main Container - using bg-gray-50 for a clean light gray box */}
          <div className="grid grid-cols-1 lg:grid-cols-2 bg-gray-50 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.12)]">
            
            {/* Left Column: Content */}
            <div className="flex flex-col justify-center p-6 sm:p-8 md:p-12 lg:p-16 bg-white shadow-[0_0_25px_rgba(0,0,0,0.12)] z-10">
              
              {/* Primary Heading */}
              <h1 className="text-3xl md:text-4xl lg:text-[40px] leading-tight font-extrabold text-brand-charcoal mb-8 uppercase">
                Mød indehaver Marcel Godske-Hjembæk
              </h1>
              
              {/* Subheading & Paragraphs */}
              <div className="space-y-4 mb-8">
                <h2 className="text-lg font-bold text-brand-charcoal uppercase">
                  Din professionelle anlægspartner
                </h2>
                
                <p className="text-base leading-relaxed text-gray-600 font-normal">
                  Godske Byg og Anlæg er en ambitiøs enkeltmandsvirksomhed med mig, Marcel Godske-Hjembæk, som indehaver. Jeg arbejder altid med stort fokus på kvalitet, holdbarhed og præcision.
                </p>
                
                <p className="text-base leading-relaxed text-gray-600 font-normal">
                  Jeg er 100% specialiseret i jord-, grave- og anlægsarbejde.
                </p>
                
                <p className="text-base leading-relaxed text-gray-600 font-normal">
                  Ring direkte på tlf.{' '}
                  <span className="text-brand-accent font-bold whitespace-nowrap">29 24 82 63</span>{' '}
                  for en god dialog og et uforpligtende tilbud.
                </p>
              </div>
              
              {/* CTA Button */}
              <div>
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 text-brand-charcoal hover:text-black text-sm tracking-wider font-extrabold transition-colors duration-200 cursor-pointer uppercase group"
                >
                  Læs mere om os
                  <ArrowRight className="h-4 w-4 text-brand-charcoal transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
              
            </div>
            
            {/* Right Column: Image Background bleeding completely to the edge */}
            <div className="relative min-h-[350px] lg:min-h-full overflow-hidden">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/b6ab12dc-9087-41bb-8b7f-45b54510818a/81077650-1562-4c98-89c4-8d102948411e.jpg" 
                alt="Marcel Godske-Hjembæk - Godske Byg & Anlæg" 
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            </div>
            
          </div>
        </div>
      </section>

      {/* Selected Projects Section */}
      <section className="bg-white py-20 sm:py-28 border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-charcoal sm:text-4xl mt-2 uppercase">
                Udvalgte projekter
              </h2>
            </div>
          </div>

          <div className="border border-gray-200 bg-gray-50 p-8 sm:p-12 text-center max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-brand-charcoal uppercase mb-3">
              Projekter på vej
            </h3>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
              Vi opdaterer løbende siden med billeder og beskrivelser af vores seneste jord-, grave- og anlægsarbejde. Kontakt os gerne, hvis du ønsker referencer på lignende opgaver.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 bg-brand-charcoal hover:bg-black text-white text-xs font-extrabold tracking-wider uppercase transition-colors duration-200"
            >
              Kontakt os for referencer
            </Link>
          </div>
        </div>
      </section>

      {/* Work Process Section */}
      <section className="bg-gray-50 py-20 sm:py-28 border-t border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Left side: Image */}
            <div className="order-2 lg:order-1 lg:col-span-5 relative min-h-[350px] lg:min-h-full overflow-hidden">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/b6ab12dc-9087-41bb-8b7f-45b54510818a/gravemaskine-arbejde.jpg"
                alt="Arbejdsproces hos Godske Byg & Anlæg"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-brand-charcoal/10" />
            </div>

            {/* Right side: Process boxes */}
            <div className="order-1 lg:order-2 lg:col-span-7 flex flex-col justify-center">
              <div className="mb-10">
                <h2 className="font-display text-3xl font-bold tracking-tight text-brand-charcoal sm:text-4xl mt-2 uppercase">
                  Arbejdsprocessen trin for trin
                </h2>
                <p className="mt-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                  Vi sikrer et trygt og gennemskueligt forløb fra den første samtale, til det færdige arbejde afleveres millimeterpræcist på din byggegrund.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {processSteps.map((step) => (
                  <div
                    key={step.step}
                    className="relative z-10 rounded-none border border-gray-200 bg-white p-6 sm:p-8 flex flex-col justify-between"
                  >
                    <div>
                      <div className="font-display text-3xl font-extrabold text-brand-charcoal mb-4">
                        {step.step}
                      </div>
                      <h3 className="text-base font-bold text-brand-charcoal mb-2 uppercase">{step.title}</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white py-12 sm:py-16 border-y border-gray-100 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-brand-charcoal sm:text-4xl mt-2 uppercase">
                Hvad vores kunder siger
              </h2>
            </div>

            {/* Navigation Arrows (Desktop) */}
            <div className="hidden md:flex items-center gap-3 mt-6 md:mt-0">
              <button
                onClick={prevTestimonial}
                className="inline-flex h-12 w-12 items-center justify-center border border-brand-accent bg-brand-accent text-white transition-all hover:bg-brand-charcoal hover:border-brand-charcoal hover:scale-105 active:scale-95 cursor-pointer"
                aria-label="Forrige anmeldelse"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={nextTestimonial}
                className="inline-flex h-12 w-12 items-center justify-center border border-brand-accent bg-brand-accent text-white transition-all hover:bg-brand-charcoal hover:border-brand-charcoal hover:scale-105 active:scale-95 cursor-pointer"
                aria-label="Næste anmeldelse"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Slider Wrapper */}
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out -mx-3"
              style={{
                transform: `translateX(-${testimonialIndex * (100 / visibleCount)}%)`,
              }}
            >
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="w-full sm:w-1/2 lg:w-1/3 shrink-0 px-3"
                >
                  <div className="h-full bg-transparent p-4 sm:p-6 flex flex-col justify-between min-h-[240px]">
                    <div>
                      <div className="flex items-center gap-1 mb-4 text-brand-accent">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-current" />
                        ))}
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed italic mb-6">
                        "{t.text}"
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-brand-charcoal uppercase">{t.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows (Mobile) */}
          <div className="flex md:hidden items-center justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="inline-flex h-12 w-12 items-center justify-center border border-brand-accent bg-brand-accent text-white transition-all hover:bg-brand-charcoal hover:border-brand-charcoal cursor-pointer"
              aria-label="Forrige anmeldelse"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={nextTestimonial}
              className="inline-flex h-12 w-12 items-center justify-center border border-brand-accent bg-brand-accent text-white transition-all hover:bg-brand-charcoal hover:border-brand-charcoal cursor-pointer"
              aria-label="Næste anmeldelse"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
